import { useEffect, useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Box, Card, CardActionArea, Grid, Stack, Typography } from '@mui/material'
import Page from '../components/Page'
import ClimateHazardIcon from '../components/climateAnalytics/common/ClimateHazardIcon'
import {
  getAllClimateHazardMethodologies,
  getClimateHazardMethodology,
} from '../components/climateAnalytics/common/hazardConfig'
import MethodologyContent from '../components/methodology/MethodologyContent'
import MethodologyTOC from '../components/methodology/MethodologyTOC'
import siteMap from '../siteMap'

function HazardPicker() {
  const history = useHistory()
  const hazards = useMemo(() => getAllClimateHazardMethodologies(), [])

  return (
    <Page size="lg" title="Climate Analytics Methodology" back>
      <Grid container spacing={2}>
        {hazards.map(({ code, config }) => (
          <Grid item xs={12} sm={6} md={4} key={code}>
            <Card variant="outlined">
              <CardActionArea
                onClick={() => history.push(siteMap.Methodology.getPath(code))}
                sx={{ p: 2.5, height: '100%' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <ClimateHazardIcon hazardCode={code} size={44} iconSize={24} />
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {config.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {config.sections.length > 0 ? `${config.sections.length} sections` : 'Overview'}
                    </Typography>
                  </Stack>
                </Stack>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Page>
  )
}

function HazardMethodology({ hazardCode }: { hazardCode: string }) {
  const location = useLocation()
  const history = useHistory()
  const methodology = getClimateHazardMethodology(hazardCode)
  const anchor = location.hash ? location.hash.slice(1) : undefined

  useEffect(() => {
    // Reset the document scroll when switching hazards / loading without an anchor.
    if (!anchor) window.scrollTo({ top: 0 })
  }, [hazardCode, anchor])

  if (!methodology) {
    return (
      <Page size="lg" title="Methodology" back>
        <Typography variant="body1" color="text.secondary">
          No methodology is published for this hazard.
        </Typography>
      </Page>
    )
  }

  const hrefBase = siteMap.Methodology.getPath(hazardCode)
  const handleSelectAnchor = (id: string) => {
    history.replace(`${hrefBase}#${id}`)
  }

  return (
    <Page size="lg" title={methodology.label} back>
      <Grid container spacing={4}>
        {methodology.sections.length > 0 && (
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 88 }}>
              <MethodologyTOC
                hazardCode={hazardCode}
                activeAnchor={anchor}
                hrefBase={hrefBase}
                onSelect={handleSelectAnchor}
                variant="page"
              />
            </Box>
          </Grid>
        )}
        <Grid item xs={12} md={methodology.sections.length > 0 ? 9 : 12}>
          <MethodologyContent hazardCode={hazardCode} anchor={anchor} variant="page" />
        </Grid>
      </Grid>
    </Page>
  )
}

export default function Methodology() {
  const { hazard } = useParams<{ hazard?: string }>()
  if (!hazard) return <HazardPicker />
  return <HazardMethodology hazardCode={hazard} />
}
